import { Bell, BellOff, BellRing, Smartphone, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { usePushPermission } from '@/hooks/use-push-notifications'
import { usePwaInstall } from '@/hooks/use-pwa-install'
import { isFirebaseConfigured } from '@/lib/firebase'

const DENIED_DISMISSED_KEY = 'ssma-push-denied-dismissed'

interface PushNotificationsCardProps {
	/** banner: na home, só quando falta decidir ou foi negado (dispensável). section: no perfil, sempre. */
	variant: 'banner' | 'section'
}

// Depois de negar, só o usuário consegue reverter; o caminho muda por plataforma
function getDeniedHelpKey() {
	const ua = navigator.userAgent
	if (/iPad|iPhone|iPod/.test(ua)) return 'push.deniedIos'
	if (/Android/.test(ua)) return 'push.deniedAndroid'
	if (/Safari/.test(ua) && !/Chrome|Chromium|Edg/.test(ua)) {
		return 'push.deniedSafari'
	}
	return 'push.deniedDefault'
}

function readDismissed() {
	try {
		return localStorage.getItem(DENIED_DISMISSED_KEY) === '1'
	} catch {
		return false
	}
}

export function PushNotificationsCard({ variant }: PushNotificationsCardProps) {
	const { t } = useTranslation()
	const { permission, enable, resync, isPending } = usePushPermission()
	const { platform, install } = usePwaInstall()
	const [deniedDismissed, setDeniedDismissed] = useState(readDismissed)

	// iOS só tem push no app instalado; no Safari comum a API nem existe
	const needsIosInstall =
		permission === 'unsupported' && platform === 'ios' && isFirebaseConfigured

	if (permission === 'unsupported' && !needsIosInstall) return null

	const isBanner = variant === 'banner'
	if (isBanner && needsIosInstall) return null // a home já tem o banner de instalar
	if (isBanner && permission === 'granted') return null
	if (isBanner && permission === 'denied' && deniedDismissed) return null

	const dismissDenied = () => {
		try {
			localStorage.setItem(DENIED_DISMISSED_KEY, '1')
		} catch {
			// sem storage: o aviso volta na próxima visita
		}
		setDeniedDismissed(true)
	}

	const isGranted = permission === 'granted'
	const isDenied = permission === 'denied'
	const Icon = needsIosInstall
		? Smartphone
		: isDenied
			? BellOff
			: isGranted
				? BellRing
				: Bell

	let description = t('push.description')
	if (needsIosInstall) description = t('push.needsIosInstall')
	else if (isDenied) description = t(getDeniedHelpKey())
	else if (isGranted) description = t('push.granted')

	return (
		<div className="mb-6 flex items-center gap-3 rounded-lg border border-kaefer-light-silver bg-white p-4">
			<Icon
				className={`size-5 shrink-0 ${isDenied ? 'text-kaefer-pumpkin-orange' : 'text-kaefer-sapphire-blue'}`}
			/>
			<div className="min-w-0 flex-1">
				<p className="font-semibold text-kaefer-dark-grey text-sm">
					{isDenied ? t('push.deniedTitle') : t('push.title')}
				</p>
				<p className="text-kaefer-medium-grey text-xs">{description}</p>
			</div>
			{needsIosInstall && (
				<Button onClick={install} size="sm">
					{t('push.howToInstall')}
				</Button>
			)}
			{!needsIosInstall && !isDenied && (
				<Button
					disabled={isPending}
					onClick={isGranted ? resync : enable}
					size="sm"
					variant={isGranted ? 'outline' : 'default'}
				>
					{isGranted ? t('push.resync') : t('push.enable')}
				</Button>
			)}
			{isBanner && isDenied && (
				<Button
					aria-label={t('pwa.dismiss')}
					className="size-8 shrink-0 text-kaefer-medium-grey"
					onClick={dismissDenied}
					size="icon"
					variant="ghost"
				>
					<X className="size-4" />
				</Button>
			)}
		</div>
	)
}
